(() => {
  const SELECTORS = {
    quickAdd: '[data-lc-quick-add]',
    quickToggle: '[data-lc-quick-toggle]',
    dotsHost: '.product-section .product__media-list',
    productRoot: '[data-lc-product-root]',
    sticky: '[data-lc-sticky-atc]'
  };

  const closeQuickAdds = (except) => {
    document.querySelectorAll(`${SELECTORS.quickAdd}.is-open`).forEach((quickAdd) => {
      if (quickAdd === except) return;
      quickAdd.classList.remove('is-open');
      quickAdd.querySelector(SELECTORS.quickToggle)?.setAttribute('aria-expanded', 'false');
    });
  };

  // The plus on a card photo opens its sizes; a size (or the plus itself on a
  // product without sizes) clicks the form's hidden Add to Bag button, so the
  // add runs through the same cart path as the product page. One delegated
  // listener covers cards added later by infinite scroll and recommendations.
  const onQuickAddClick = (event) => {
    const quickAdd = event.target.closest(SELECTORS.quickAdd);
    if (!quickAdd) {
      closeQuickAdds();
      return;
    }
    const toggle = event.target.closest(SELECTORS.quickToggle);
    const size = event.target.closest('.lc-quick-add__size[data-variant-id]');
    if (!toggle && !size) return;
    event.preventDefault();
    event.stopPropagation();
    const idInput = quickAdd.querySelector('[name="id"]');
    const addButton = quickAdd.querySelector('[name="add"]');
    const sizes = quickAdd.querySelector('.lc-quick-add__sizes');
    if (!idInput || !addButton) return;

    if (toggle) {
      if (!sizes) {
        addButton.click();
        return;
      }
      const opening = !quickAdd.classList.contains('is-open');
      closeQuickAdds(quickAdd);
      quickAdd.classList.toggle('is-open', opening);
      toggle.setAttribute('aria-expanded', String(opening));
      return;
    }

    if (size.disabled) return;
    idInput.value = size.dataset.variantId;
    addButton.click();
    sizes.querySelectorAll('.is-added').forEach((added) => added.classList.remove('is-added'));
    size.classList.add('is-added');
    window.setTimeout(() => {
      size.classList.remove('is-added');
      closeQuickAdds();
    }, 900);
  };

  const initGalleryDots = (scope = document) => {
    scope.querySelectorAll(SELECTORS.dotsHost).forEach((host) => {
      if (host.dataset.lcDotsReady === 'true') return;
      const sliderElement = host.querySelector('.js-media-list');
      if (!sliderElement) return;

      const mountDots = () => {
        const swiper = sliderElement.swiper;
        if (!swiper || host.dataset.lcDotsReady === 'true') return false;
        const slides = [...swiper.slides].filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
        if (slides.length < 2) return true;
        host.dataset.lcDotsReady = 'true';
        const dots = document.createElement('div');
        dots.className = 'lc-mobile-product-dots';
        dots.setAttribute('aria-label', 'Product images');
        slides.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'lc-mobile-product-dot';
          dot.setAttribute('aria-label', `Image ${index + 1}`);
          dot.addEventListener('click', () => swiper.slideToLoop ? swiper.slideToLoop(index) : swiper.slideTo(index));
          dots.appendChild(dot);
        });
        host.insertAdjacentElement('afterend', dots);
        const update = () => {
          const activeIndex = swiper.realIndex ?? swiper.activeIndex;
          [...dots.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === activeIndex)));
        };
        swiper.on('slideChange', update);
        update();
        return true;
      };

      if (!mountDots()) {
        let retries = 0;
        const timer = window.setInterval(() => {
          retries += 1;
          if (mountDots() || retries > 30) window.clearInterval(timer);
        }, 100);
      }
    });
  };

  /* Shopify products can mix portrait, square, and landscape assets. Make the
     active slide determine the carousel height on every product template. */
  const initAdaptiveProductGalleries = (scope = document) => {
    scope.querySelectorAll('.product-section .js-media-list').forEach((sliderElement) => {
      if (sliderElement.dataset.lcAdaptiveGalleryReady === 'true') return;

		const mount = () => {
			const swiper = sliderElement.swiper;
			if (!swiper || swiper.destroyed) return false;
			let viewportWidth = document.documentElement.clientWidth;

        sliderElement.dataset.lcAdaptiveGalleryReady = 'true';
        swiper.params.autoHeight = true;

        const syncHeight = () => {
          if (!window.matchMedia('(max-width: 989px)').matches) return;
          window.requestAnimationFrame(() => {
            if (swiper.destroyed) return;
            const activeSlide = swiper.slides[swiper.activeIndex];
            const image = activeSlide?.querySelector('.product__media:not(.product__media-deferred) img');
            const width = activeSlide?.getBoundingClientRect().width || sliderElement.clientWidth;
            const fallbackPadding = Number.parseFloat(
              activeSlide?.querySelector('.product__media')?.style.getPropertyValue('--lc-media-padding-top')
            );
            const ratio = image?.naturalWidth
              ? image.naturalHeight / image.naturalWidth
              : (fallbackPadding ? fallbackPadding / 100 : 0);
            const height = width * ratio;
            if (Number.isFinite(height) && height > 0) {
              sliderElement.style.setProperty('--lc-active-media-height', `${Math.round(height)}px`);
            }
            swiper.updateAutoHeight(260);
          });
        };

        sliderElement.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', syncHeight, { once: true });
        });
        swiper.on('slideChangeTransitionStart', syncHeight);
        swiper.on('slideChangeTransitionEnd', syncHeight);
        swiper.on('imagesReady', syncHeight);
		// Ignore height-only viewport changes generated by mobile browser chrome
		// during vertical scrolling. Heights still update after a horizontal slide
		// change, image load, or a real width/orientation change.
		window.addEventListener('resize', () => {
			const nextViewportWidth = document.documentElement.clientWidth;
			if (nextViewportWidth === viewportWidth) return;
			viewportWidth = nextViewportWidth;
			window.setTimeout(syncHeight, 250);
		}, { passive: true });
        syncHeight();
        return true;
      };

      if (mount()) return;
      let retries = 0;
      const timer = window.setInterval(() => {
        retries += 1;
        if (mount() || retries > 30) window.clearInterval(timer);
      }, 100);
    });
  };

  const initStickyAtc = (scope = document) => {
    scope.querySelectorAll(SELECTORS.productRoot).forEach((root) => {
      if (root.dataset.lcStickyReady === 'true') return;
      const sticky = root.querySelector(SELECTORS.sticky);
      if (!sticky || !sticky.dataset.productForm) return;
      const mainForm = root.querySelector(`#${CSS.escape(sticky.dataset.productForm)}`);
      const stickyForm = sticky?.querySelector('form');
      const stickySelect = sticky?.querySelector('[data-lc-sticky-variant]');
      const stickyButton = sticky?.querySelector('[name="add"]');
      const mainInput = mainForm?.querySelector('[name="id"]');
      if (!sticky || !stickyForm || !stickyButton || !mainInput) return;
      root.dataset.lcStickyReady = 'true';

      const syncFromMain = () => {
        if (!stickySelect) {
          stickyButton.disabled = mainInput.disabled;
          return;
        }
        const option = stickySelect.querySelector(`option[value="${CSS.escape(mainInput.value)}"]`);
        if (!option) return;
        stickySelect.value = mainInput.value;
        stickyButton.disabled = option.disabled;
        sticky.querySelector('[data-lc-sticky-price]').textContent = option.dataset.price || '';
      };
      const syncFromSticky = () => {
        const option = stickySelect.options[stickySelect.selectedIndex];
        stickyForm.querySelector('[name="id"]').value = stickySelect.value;
        stickyButton.disabled = option.disabled;
        sticky.querySelector('[data-lc-sticky-price]').textContent = option.dataset.price || '';
      };
      mainInput.addEventListener('change', syncFromMain);
      stickySelect?.addEventListener('change', syncFromSticky);
      syncFromMain();

      // Match the mobile reference behaviour: the bar is present on initial
      // load, disappears while the page's own Add to Cart button is visible,
      // then returns once that button has been scrolled past.
      const mainButton = mainForm.querySelector('[name="add"]');
      const setVisible = (visible) => {
        sticky.classList.toggle('is-visible', visible);
        const shown = visible && sticky.offsetParent !== null;
        document.documentElement.classList.toggle('lc-sticky-atc-on', shown);
        document.documentElement.style.setProperty('--lc-sticky-atc-height', shown ? `${sticky.offsetHeight}px` : '0px');
      };
      const updateVisibility = () => {
        if (!mainButton) return setVisible(true);
        const rect = mainButton.getBoundingClientRect();
        setVisible(!(rect.top < window.innerHeight && rect.bottom > 0));
      };
      let hasScrolled = false;
      let ticking = false;
      window.addEventListener('scroll', () => {
        hasScrolled = true;
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          updateVisibility();
        });
      }, { passive: true });
      window.addEventListener('resize', () => {
        if (hasScrolled) updateVisibility();
        else setVisible(true);
      });
      setVisible(true);
    });
  };

  const init = (scope = document) => {
    initAdaptiveProductGalleries(scope);
    initGalleryDots(scope);
    initStickyAtc(scope);
  };

  document.addEventListener('click', onQuickAddClick);
  // Light up the size under the finger before it is released, so shoppers on
  // phones see which size they are about to add.
  const pressSize = (event) => {
    document.querySelectorAll('.lc-quick-add__size.is-pressed').forEach((size) => size.classList.remove('is-pressed'));
    if (event.type !== 'pointerdown') return;
    event.target.closest?.('.lc-quick-add__size:not([disabled])')?.classList.add('is-pressed');
  };
  ['pointerdown', 'pointerup', 'pointercancel'].forEach((type) => document.addEventListener(type, pressSize, { passive: true }));
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
